import { CSS_CLASSES } from "../../constants/app";
import { ChevronDown } from "lucide-react";
import type { QuestionnaireAnswerValue } from "../../types";
import QuestionStep from "./QuestionStep";
import { QuestionCardSkeleton } from "./QuestionnaireSkeletons";
import type { Direction, FlatQuestionItem, SectionStat } from "./questionnaire.types";

interface QuestionnaireFormViewProps {
  sectionTitle: string;
  sectionStats: SectionStat[];
  activeQuestionnaireId: string;
  optionalGroupExpanded: boolean;
  optionalGroupLabel: string;
  onToggleOptionalGroup: () => void;
  onSectionSelect: (questionnaireId: string) => void;
  currentVisibleIndex: number;
  onBack: () => void;
  onBackToHistory: () => void;
  sectionSwitchLoading: boolean;
  outgoingItem: FlatQuestionItem | null;
  isTransitioning: boolean;
  enterActive: boolean;
  direction: Direction;
  outgoingAnswer: QuestionnaireAnswerValue;
  section: FlatQuestionItem["section"];
  question: FlatQuestionItem["question"];
  currentAnswer: QuestionnaireAnswerValue;
  updateAnswer: (value: QuestionnaireAnswerValue) => void;
  onClearSingleSelect: () => void;
  toggleMultiOption: (value: string) => void;
  updateOptionText: (optionValue: string, textValue: string) => void;
  getOptionText: (optionValue: string) => string;
  validationError: string;
  submitError: string | null;
  currentQuestionIndexInSection: number;
  currentSectionQuestionsLength: number;
  sectionProgress: number;
  onNext: () => void;
  submitLoading: boolean;
  isLastVisibleQuestion: boolean;
  hasMoreSections: boolean;
}

export default function QuestionnaireFormView({
  sectionTitle,
  sectionStats,
  activeQuestionnaireId,
  optionalGroupExpanded,
  optionalGroupLabel,
  onToggleOptionalGroup,
  onSectionSelect,
  currentVisibleIndex,
  onBack,
  onBackToHistory,
  sectionSwitchLoading,
  outgoingItem,
  isTransitioning,
  enterActive,
  direction,
  outgoingAnswer,
  section,
  question,
  currentAnswer,
  updateAnswer,
  onClearSingleSelect,
  toggleMultiOption,
  updateOptionText,
  getOptionText,
  validationError,
  submitError,
  currentQuestionIndexInSection,
  currentSectionQuestionsLength,
  sectionProgress,
  onNext,
  submitLoading,
  isLastVisibleQuestion,
  hasMoreSections
}: QuestionnaireFormViewProps) {
  const requiredSectionStats = sectionStats.filter((item) => !item.isOptionalSection);
  const optionalSectionStats = sectionStats.filter((item) => item.isOptionalSection);
  const optionalCompleted = optionalSectionStats.filter((item) => item.status === "completed").length;

  const renderSectionButton = (sectionItem: SectionStat) => {
    const isActive = sectionItem.questionnaireId === activeQuestionnaireId;
    const statusDot =
      sectionItem.status === "completed"
        ? "bg-rag-green"
        : sectionItem.status === "in_progress"
          ? "bg-rag-amber"
          : "bg-guardian-20";

    return (
      <button
        type="button"
        key={sectionItem.questionnaireId}
        onClick={() => onSectionSelect(sectionItem.questionnaireId)}
        className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${
          isActive
            ? "border-guardian-20 bg-guardian-10"
            : "border-guardian-20 bg-white hover:bg-guardian-10/60"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-guardian-body">{sectionItem.title}</p>
          <span className={`h-2.5 w-2.5 rounded-full ${statusDot}`} />
        </div>
        <p className={`mt-1 text-xs ${CSS_CLASSES.TEXT.MUTED}`}>
          {sectionItem.answered}/{sectionItem.total} answered
        </p>
      </button>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 lg:h-full lg:flex-row lg:gap-6">
      <aside className="flex w-full max-h-[176px] min-h-0 shrink-0 flex-col overflow-hidden border-b border-guardian-20 pb-4 sm:max-h-[188px] lg:h-full lg:max-h-full lg:w-72 lg:flex-shrink-0 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-4">
        <h3 className="mb-3 shrink-0 text-sm font-bold tracking-tight text-guardian-body">{sectionTitle}</h3>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:block lg:space-y-2">
            {requiredSectionStats.map((sectionItem) => renderSectionButton(sectionItem))}

            {optionalSectionStats.length > 0 && (
              <div className="border-t border-guardian-20 pt-2 sm:col-span-2 lg:col-span-1">
                <button
                  type="button"
                  onClick={onToggleOptionalGroup}
                  className="flex w-full items-center justify-between gap-2 rounded-xl border border-guardian-20 px-3 py-2 text-left transition-colors hover:bg-guardian-10/60"
                >
                  <div>
                    <p className="text-sm font-medium text-guardian-body">{optionalGroupLabel}</p>
                    <p className={`mt-1 text-xs ${CSS_CLASSES.TEXT.MUTED}`}>
                      {optionalCompleted}/{optionalSectionStats.length} completed
                    </p>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 text-guardian-header transition-transform ${
                      optionalGroupExpanded ? "rotate-180" : ""
                    }`}
                    aria-hidden
                  />
                </button>

                {optionalGroupExpanded && (
                  <div className="mt-2 space-y-2">
                    {optionalSectionStats.map((sectionItem) => renderSectionButton(sectionItem))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:overflow-hidden">
        <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBack}
            disabled={currentVisibleIndex === 0}
            className="inline-flex items-center gap-1 text-sm font-semibold text-rag-green transition-colors disabled:cursor-not-allowed disabled:text-guardian-20"
          >
            <span aria-hidden>←</span>
            <span>Back</span>
          </button>
          <button
            type="button"
            onClick={onBackToHistory}
            className={`whitespace-nowrap ${CSS_CLASSES.BUTTONS.SECONDARY_PATIENT} min-h-8 px-3 py-1.5 text-xs`}
          >
            Back to history
          </button>
        </div>

        <div className="relative isolate min-h-0 flex-1 overflow-x-hidden overflow-y-hidden">
          {sectionSwitchLoading ? (
            <QuestionCardSkeleton />
          ) : (
            <>
              {outgoingItem && isTransitioning && (
                <div
                  className={`pointer-events-none absolute inset-0 z-10 transform-gpu rounded-xl border border-guardian-20 p-4 shadow-sm transition-[opacity,transform] duration-500 ease-in-out sm:p-5 ${
                    enterActive
                      ? direction === "next"
                        ? "-translate-x-full opacity-0"
                        : "translate-x-full opacity-0"
                      : "translate-x-0 opacity-100"
                  }`}
                >
                  <QuestionStep
                    section={outgoingItem.section}
                    question={outgoingItem.question}
                    answer={outgoingAnswer}
                    isActive={!enterActive}
                    onSingleSelect={() => {}}
                    onClearSingleSelect={() => {}}
                    onMultiToggle={() => {}}
                    onOptionTextChange={() => {}}
                    getOptionTextValue={() => ""}
                    onTextChange={() => {}}
                    onNumberChange={() => {}}
                  />
                </div>
              )}

              <div
                key={question.questionId}
                className={`relative z-20 transform-gpu rounded-xl border border-guardian-20 p-4 transition-[opacity,transform] duration-500 ease-in-out sm:p-5 ${
                  isTransitioning
                    ? enterActive
                      ? "translate-x-0 opacity-100 scale-100 shadow-sm"
                      : direction === "next"
                        ? "translate-x-full opacity-0 scale-95"
                        : "-translate-x-full opacity-0 scale-95"
                    : "translate-x-0 opacity-100 scale-100"
                }`}
              >
                <QuestionStep
                  section={section}
                  question={question}
                  answer={currentAnswer}
                  isActive={!isTransitioning || enterActive}
                  onSingleSelect={(value) => {
                    if (currentAnswer === value) {
                      onClearSingleSelect();
                    } else {
                      updateAnswer(value);
                    }
                  }}
                  onClearSingleSelect={onClearSingleSelect}
                  onMultiToggle={(value) => toggleMultiOption(value)}
                  onOptionTextChange={(optionValue, textValue) =>
                    updateOptionText(optionValue, textValue)
                  }
                  getOptionTextValue={(optionValue) => getOptionText(optionValue)}
                  onTextChange={(value) => updateAnswer(value)}
                  onNumberChange={(value) =>
                    updateAnswer(value === "" ? null : Number(value))
                  }
                />

                {validationError && (
                  <p className={`mt-3 ${CSS_CLASSES.TEXT.ERROR}`}>{validationError}</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-guardian-20 pt-4">
          {submitError && (
            <p className={`mb-2 ${CSS_CLASSES.TEXT.ERROR}`}>{submitError}</p>
          )}
          <div className="flex flex-col gap-4 p-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="mb-1 text-xs font-semibold text-guardian-header">
                {Math.max(currentQuestionIndexInSection + 1, 1)} of {currentSectionQuestionsLength}
              </p>
              <div className="h-1 w-28 overflow-hidden rounded-full bg-guardian-10">
                <div
                  className="h-full bg-rag-green transition-all duration-300 ease-out"
                  style={{ width: `${sectionProgress}%` }}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={onNext}
              disabled={submitLoading}
              className={`${CSS_CLASSES.BUTTONS.PRIMARY_MODAL} whitespace-nowrap sm:min-w-[150px]`}
            >
              {submitLoading
                ? "Submitting..."
                : isLastVisibleQuestion
                  ? hasMoreSections
                    ? "Submit & Next"
                    : "Submit"
                  : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
